
const EXPECTED_CREDITS = 120;

/**
 * Check the module codes against the option rules
 *
 * @param moduleData list of module data object (term and credits needed)
 * @param rules list of option rules to check against
 * @param modules_codes list of selected modules
 * @return list of errors found (if empty list, then passed). Errors are
 * in pairs of strings [title, details]
 */
function checkOptionRules(modulesData, rules, moduleCodes) {
  const remainingModules = new Set(moduleCodes);
  const localErrors = [];
  const globalErrors = []

  const modMap = {}
  for (var mod of modulesData) {
    modMap[mod.mod_code] = mod;
  }

  /* -- CHECK GLOBAL RULES -- */
  globalErrors.push(...checkMaxStrandRules(modMap, rules, remainingModules));
  globalErrors.push(...checkCredits(modMap, rules, remainingModules));
  globalErrors.push(...checkDisallowedRules(rules, remainingModules));

  /* -- CHECK LOCAL RULES -- */
  localErrors.push(...checkCoreRules(rules, remainingModules));
  localErrors.push(...checkDiscAltRules(rules, remainingModules));
  localErrors.push(...checkStrandRules(modMap, rules, remainingModules));
  localErrors.push(...checkOptionalRules(rules, remainingModules));

  // put local errors first as they're more relevant and the UI is only
  // displaying the first error at the moment
  return localErrors.concat(globalErrors);
}

/**
 * Checks the modules picked against the core rules.
 *
 * Removes modules needed to satisfy these rules from remainingModules
 * set
 *
 * @param {*} rules - the option rules of the programme and stage
 * @param {*} remainingModules - the set of remaining modules to be checked.
 * @return {*} errors list of errors found (empty means ok)
 */
function checkCoreRules(rules, remainingModules) {
  const errors = [];
  try {
    for (const i in rules) {
      if (rules[i]) {
        const rule = rules[i];
        if (rule.constraint_type == 'CORE') {
          if (!remainingModules.delete(rule.mod_code_pattern)) {
            errors.push([
              'Missing core module ' + rule.mod_code_pattern,
              'Core module ' + rule.mod_code_pattern + " is required on your degree",
            ]);
          }
        }
      }
    }
  } catch (e) {
    console.log(e);
  }
  return errors;
}

/**
 * Checks the modules picked against the disc_alt rules.
 *
 * Removes modules needed to satisfy these rules from remainingModules
 * set
 *
 * @param {*} rules - the option rules of the programme and stage
 * @param {*} remainingModules - the set of remaining modules to be checked.
 * @return {*} errors list of errors found (empty means ok)
 */
function checkDiscAltRules(rules, remainingModules) {
  const errors = [];
  try {
    for (const i in rules) {
      if (rules[i]) {
        const rule = rules[i];
        if (rule.constraint_type == 'DISC_ALT') {
          /* Format of mod code pattern:
          CODE1,CODE2,... */
          const alts = rule.mod_code_pattern.split(",");
          var found = false;
          for (var alt_code of alts) {
            if (remainingModules.delete(alt_code)) {
              found = true;
              break;
            }
          }
          if (!found) {
            errors.push([
              'Missing one of ' + rule.mod_code_pattern,
              'You must have at least one of the modules ' + rule.mod_code_pattern,
            ]);
          }
        }
      }
    }
  } catch (e) {
    console.log(e);
  }
  return errors;
}

/**
 * Checks the optional modules picked against the strand rules.
 *
 * Removes modules needed to satisfy these rules from remainingModules
 * set
 *
 * @param {*} modData - map from mod code to module object (term and
 * credits needed)
 * @param {*} rules - the option rules of the programme and stage
 * @param {*} remainingModules - the set of remaining modules to be checked.
 * @return {*} errors list of errors found (empty means ok)
 */
function checkStrandRules(modData, rules, remainingModules) {
  const errors = [];
  try {
    for (const i in rules) {
      if (rules[i]) {
        const rule = rules[i];
        if (rule.constraint_type == 'STRAND') {
          /* Format of mod code pattern:
          STRAND,PREFIX1,PREFIX_N... */
          const splitPattern = rule.mod_code_pattern.trim().split(',');
          /* The specific strand */
          const strand = splitPattern[0];
          /* The strand's prefixes */
          const prefixes = splitPattern.slice(1, splitPattern.length);

          const strandModules = getModCodePrefixMatches(
            getStrandModules(modData, strand, remainingModules),
            prefixes
          );

          /* Check if strand modules is less than minimum requirement */
          if (strandModules.length < rule.min_quantity) {
            errors.push([
              'Not enough modules from strand ' + strand,
              rule.min_quantity +
                ' modules of this strand needed but ' +
                strandModules.length +
                ' chosen',
            ]);
          }

          /* Remove minimum number of strand modules */
          for (const modCode in strandModules.slice[rule.min_quantity]) {
            if (modCode) {
              remainingModules.delete(modCode);
            }
          }
        }
      }
    }
  } catch (e) {
    console.log(e);
  }
  return errors;
}

/**
 * Checks the optional modules picked against the optional rules.
 *
 * Removes modules needed to satisfy these rules from remainingModules
 * set
 *
 * @param {*} rules - the option rules of the programme and stage
 * @param {*} remainingModules - the remaining modules to be checked.
 * @return {*} errors - if any errors found from validation check.
 */
function checkOptionalRules(rules, remainingModules) {
  const errors = [];
  try {
    for (const i in rules) {
      if (rules[i]) {
        const rule = rules[i];
        if (rule.constraint_type == 'OPTS') {
          /* Prefixes of module code pattern */
          const prefixes = rule.mod_code_pattern.trim().split(',');

          const optMods = getModCodePrefixMatches(remainingModules, prefixes)

          /* Check if optional modules is less than minimum requirement */
          if (optMods.length < rule.min_quantity) {
            errors.push([
              'Not enough optional modules (any strand) beginning ' +
                rule.mod_code_pattern,
              rule.min_quantity +
                ' modules (any strand) needed but ' +
                optMods.length +
                ' chosen',
            ]);
          }

          /* Remove minimum number of optional modules */
          for (const modCode in optMods.slice[rule.min_quantity]) {
            if (modCode) {
              remainingModules.delete(modCode);
            }
          }
        }
      }
    }
  } catch (e) {
    console.log(e);
  }
  return errors;
}

/**
 * Checks the optional modules picked against the max_strand rules.
 *
 * Does not change choices argument
 *
 * @param {*} modData - map from mod code to module object (term and
 * credits needed)
 * @param {*} rules - the option rules of the programme and stage
 * @param {*} choices - the user's selected choices to be checked.
 * @return {*} errors -if any errors found from validation check.
 */
function checkMaxStrandRules(modData, rules, choices) {
  const errors = [];
  try {
    for (const i in rules) {
      if (rules[i]) {
        const rule = rules[i];
        if (rule.constraint_type == 'MAX_STRAND') {
          /* Format of mod code pattern:
          STRAND,PREFIX1,PREFIX_N... */
          const splitPattern = rule.mod_code_pattern.trim().split(',');
          /* The specific strand */
          const strand = splitPattern[0];
          /* The strand's prefixes */
          const prefixes = splitPattern.slice(1);

          const strandModules = getModCodePrefixMatches(
            getStrandModules(modData, strand, choices),
            prefixes
          )

          /* Check if strand modules is less than minimum requirement */
          if (strandModules.length > rule.min_quantity) {
            const diff = strandModules.length - rule.min_quantity;
            errors.push([
              'Too many modules from strand ' + strand,
              'Deselect ' + diff + ' modules of this strand',
            ]);
          }
        }
      }
    }
  } catch (e) {
    console.log(e);
  }
  return errors;
}

/**
 * Each term must have equal workload (same amount of modules).
 * @param {*} modData - map from mod code to module object (term and
 * credits needed)
 * @param {*} choices - the selected choices to check.
 * @return {*} errors - if any errors found from validation check.
 */
function checkCredits(modData, rules, choices) {
  const errors = [];
  try {
    // find floating external credits in rules
    var floatingCredits = 0
    for (const i in rules) {
      if (rules[i]) {
        const rule = rules[i];
        if (rule.constraint_type == 'EXTERNAL') {
          // assume only external rules with fixed num credits at the
          // moment
          floatingCredits = Number(rule.max_quantity)
        }
      }
    }

    /* Credits for each term */
    const credits = {
      '1': 0,
      '2': 0,
      'Both': 0,
    };
    choices.forEach((choiceModCode) => {
      /* Only add credits to term if the module exists in choices */
      if (choiceModCode in modData) {
        const mod = modData[choiceModCode];
        /* Only count credits if term is known */
        if (mod.term != null) {
          credits[mod.term] += parseInt(mod.credits);
        }
      } else {
        console.log('Warning, do not know term of ' + choiceModCode);
      }
    });

    /* Calculate term credit scores */
    const term1Credits = credits['1'] + credits['Both'] / 2;
    const term2Credits = credits['2'] + credits['Both'] / 2;

    // Check if the workload is balanced between the terms if all
    // credits known
    if (term1Credits != term2Credits && floatingCredits == 0) {
      errors.push([
        'Workload is not balanced',
        'Terms need same amount of credits; you currently have '
          + term1Credits + " in Term 1 and "
          + term2Credits + " in Term 2."
      ]);
      return errors;
    }

    if (term1Credits > EXPECTED_CREDITS / 2) {
      errors.push([
        'Workload is not balanced',
        'Terms need same amount of credits; you currently have '
          + term1Credits + " in Term 1, which is too many."
      ]);
      return errors;
    }
    if (term2Credits > EXPECTED_CREDITS / 2) {
      errors.push([
        'Workload is not balanced',
        'Terms need same amount of credits; you currently have '
          + term2Credits + " in Term 2, which is too many."
      ]);
      return errors;
    }

    // Check if there are enough total credits
    // doesn't work for YinI year at the moment
    const totalCredits = term1Credits + term2Credits + floatingCredits;
    if (totalCredits < EXPECTED_CREDITS) {
      const missingCredits = EXPECTED_CREDITS - totalCredits;
      errors.push([
        'Not enough modules selected',
        'Select ' + missingCredits + ' more credits',
      ]);
    } else if (totalCredits > EXPECTED_CREDITS) {
      const extraCredits = totalCredits - EXPECTED_CREDITS;
      errors.push([
        'Too many modules selected',
        'Deselect ' + extraCredits + ' credits',
      ]);
    }
  } catch (e) {
    console.log(e);
  }

  return errors;
}

/**
 * Checks the optional modules picked against the disallowed rules.
 * @param {*} rules - the option rules of the programme and stage
 * @param {*} choices - the user's selected choices to be checked.
 * @return {*} errors -if any errors found from validation check.
 */
function checkDisallowedRules(rules, choices) {
  const errors = [];
  try {
    /* Disallowed modules */
    const disallowedMods = [];
    for (const i in rules) {
      if (rules[i]) {
        const rule = rules[i];
        if (rule.constraint_type == 'DISALLOWED') {
          if (choices.has(rule.mod_code_pattern)) {
            disallowedMods.push(choices[i].mod_code);
          }
        }
      }
    }

    if (disallowedMods.length > 0) {
      errors.push([
        'Disallowed modules included!',
        'Module(s): ' + disallowedMods + ' not allowed on this programme',
      ]);
    }
  } catch (e) {
    console.log(e);
  }
  return errors;
}

/**
 * Get modules satisfying a prefix
 *
 * Returns a list of all modules in modCodeSet that match a prefix in
 * prefixList
 */
function getModCodePrefixMatches(modCodeSet, prefixList) {
  const matches = [];
  modCodeSet.forEach(modCode => {
    /* Check if module code starts with
    any of the strand's prefixes */
    for (const prefix of prefixList) {
      if (modCode.startsWith(prefix)) {
        matches.push(modCode);
        break;
      }
    }
  });
  return matches;
}

/**
 * Returns a set of all modules in modCodeSet that match a prefix in
 * prefixList
 * @param {*} modData - map from mod code to module object (term and
 * credits needed)
 * @param {String} expected strand
 * @param {*} modCodeSet
 */
function getStrandModules(modData, strand, modCodeSet) {
  return new Set([...modCodeSet].filter(mc => {
    const mod = modData[mc]
    if (!mod) return false
    if (!mod.strands) return false
    return mod.strands.includes(strand)
  }))
}

module.exports = {
  checkOptionRules
}
